import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import api from '../services/api'
import Reveal from '../components/landing/Reveal'
import CinematicNav from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Users,
  Target,
  Layers,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'

export default function ProjectLabDetailPage() {
  const { slug } = useParams()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  // Application form
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    experience: '', portfolio_url: '', motivation: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/project-lab/projects/${slug}`)
        setProject(data.project)
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true)
        else console.error('[ProjectLab] Detail load error:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [slug])

  const handleInputChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    setSubmitError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setSubmitError('First name, last name, and email are required.')
      return
    }
    setSubmitting(true)
    setSubmitError(null)

    try {
      await api.post('/project-lab/applications', {
        project_id: project.id,
        offering_id: project.offering_id,
        mode: project.mode,
        ...form,
      })
      setSubmitted(true)
    } catch (err) {
      if (err.response?.status === 409) {
        setSubmitError('You have already applied to this project.')
      } else if (err.response?.status === 400) {
        setSubmitError(err.response.data?.error || 'Please check your information and try again.')
      } else {
        setSubmitError('Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const acceptingApplications = project && ['open', 'filling', 'upcoming'].includes(project.status)

  if (loading) {
    return (
      <div className="cinema-page min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (notFound || !project) {
    return (
      <div className="cinema-page min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-cinema-muted font-sans">Project not found.</p>
        <Link to="/project-lab" className="text-cinema-gold text-sm font-sans hover:text-white transition-colors rounded-none">
          ← Back to Project Lab
        </Link>
      </div>
    )
  }

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>{project.meta_title || `${project.title} — SMA Project Lab`}</title>
        <meta
          name="description"
          content={project.meta_description || project.short_description || `${project.title} at Synergy Multimedia Academy Project Lab`}
        />
        <link rel="canonical" href={`https://synergymultimediaacademy.com/project-lab/${project.slug}`} />
        <meta property="og:title" content={project.meta_title || `${project.title} — SMA Project Lab`} />
        <meta property="og:description" content={project.meta_description || project.short_description || ''} />
        <meta property="og:url" content={`https://synergymultimediaacademy.com/project-lab/${project.slug}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="cinema-grain" aria-hidden="true" />

      {/* Official SMA Public Navigation */}
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} onNavigate={() => {}} />

      {/* Project Header */}
      <header className="relative pt-28 pb-10 sm:pt-36 sm:pb-16 border-b border-white/5">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <Reveal>
            {/* Editorial Breadcrumb */}
            <Link
              to="/project-lab"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-cinema-muted hover:text-cinema-gold transition-colors mb-6 rounded-none"
            >
              <ArrowLeft size={13} />
              <span>All Projects</span>
            </Link>

            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-xs uppercase tracking-[0.16em] font-sans font-semibold mb-4 sm:mb-5 rounded-none block w-fit">
              <Target className="w-3.5 h-3.5" />
              <span>Project Lab · {project.discipline}</span>
            </div>

            <h1 className="cinema-headline-xl text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight">
              {project.title}
            </h1>

            <div className="mt-4 sm:mt-6 flex flex-wrap gap-x-8 gap-y-2 text-xs text-cinema-muted font-sans uppercase tracking-wider">
              <span className="flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-cinema-gold" /> {project.level}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-cinema-gold" /> {project.duration || `${project.duration_weeks} weeks`}
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-cinema-gold" /> {project.mode === 'private' ? 'Private 1:1' : `Cohort (Max ${project.capacity})`}
              </span>
            </div>
          </Reveal>
        </div>
      </header>

      {/* Main Content Layout */}
      <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-10 sm:py-16">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 xl:gap-16 items-start">

          {/* Left Column: Description, Prominent Final Outcome, Workflow, Prerequisites */}
          <div className="lg:col-span-3 space-y-10">

            {/* Description */}
            {project.description && (
              <Reveal>
                <div className="prose prose-sm prose-invert max-w-none">
                  <p className="text-sm sm:text-base text-cinema-soft/85 leading-relaxed font-sans whitespace-pre-line">
                    {project.description}
                  </p>
                </div>
              </Reveal>
            )}

            {/* ── HIGH PROMINENCE FINAL OUTCOME ── */}
            {project.final_deliverable && (
              <Reveal delay={40}>
                <div className="py-8 border-y border-cinema-gold/30">
                  <p className="text-xs font-mono uppercase tracking-[0.25em] text-cinema-gold font-semibold mb-3">
                    Target Commercial Deliverable &middot; What You Will Produce
                  </p>
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-light text-white leading-tight tracking-tight">
                    {project.final_deliverable}
                  </h2>
                  <p className="mt-3 text-xs sm:text-sm text-cinema-soft/70 font-sans max-w-2xl leading-relaxed">
                    This is not an abstract exercise. You will walk away with a verified, client-ready portfolio asset meeting strict commercial studio delivery specifications.
                  </p>
                </div>
              </Reveal>
            )}

            {/* ── WORKFLOW: STRUCTURED EDITORIAL STAGE SEQUENCE ── */}
            {project.stages && project.stages.length > 0 && (
              <Reveal delay={60}>
                <div className="pt-2">
                  <div className="flex items-baseline justify-between mb-6">
                    <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold">
                      Production Workflow
                    </h3>
                    <span className="text-[0.7rem] font-mono uppercase tracking-wider text-cinema-muted">
                      01 &rarr; {String(project.stages.length).padStart(2, '0')} Canonical Sequence
                    </span>
                  </div>

                  <div className="divide-y divide-white/5 border-t border-b border-white/5">
                    {project.stages.map((stage, i) => (
                      <div
                        key={i}
                        className="py-4 grid grid-cols-12 gap-3 sm:gap-6 items-baseline"
                      >
                        <span className="col-span-2 sm:col-span-1 font-mono text-xs sm:text-sm text-cinema-gold/80 font-medium">
                          {String(stage.order || i + 1).padStart(2, '0')}
                        </span>
                        <div className="col-span-10 sm:col-span-4">
                          <p className="text-sm sm:text-base text-white font-sans font-medium tracking-tight">
                            {stage.title}
                          </p>
                        </div>
                        <div className="col-span-12 sm:col-span-7 mt-1 sm:mt-0">
                          {stage.description && (
                            <p className="text-xs sm:text-sm text-cinema-muted font-sans leading-relaxed">
                              {stage.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}

            {/* Prerequisites */}
            {project.prerequisites && (
              <Reveal delay={80}>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-cinema-muted font-semibold mb-3">
                    Prerequisites
                  </p>
                  <p className="text-sm text-cinema-soft/80 font-sans leading-relaxed">
                    {project.prerequisites}
                  </p>
                </div>
              </Reveal>
            )}
          </div>

          {/* Right Column: Open Editorial Specifications & CTA */}
          <div className="lg:col-span-2 space-y-6">
            <Reveal>
              <div className="border-t border-b border-white/10 divide-y divide-white/5">
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Status</span>
                  <span className={`text-xs uppercase tracking-wider font-sans font-semibold ${
                    project.status === 'open' ? 'text-green-400' :
                    project.status === 'filling' ? 'text-yellow-400' :
                    project.status === 'full' ? 'text-red-400' :
                    'text-cinema-muted'
                  }`}>
                    {project.status === 'open' ? 'Open' :
                     project.status === 'filling' ? 'Filling Up' :
                     project.status === 'full' ? 'Full' :
                     project.status === 'upcoming' ? 'Coming Soon' :
                     project.status === 'in_progress' ? 'In Progress' :
                     project.status === 'completed' ? 'Completed' : project.status}
                  </span>
                </div>
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Discipline</span>
                  <span className="text-sm text-white font-sans font-medium">{project.discipline}</span>
                </div>
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Level</span>
                  <span className="text-sm text-white font-sans font-medium">{project.level}</span>
                </div>
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Duration</span>
                  <span className="text-sm text-white font-sans">{project.duration || `${project.duration_weeks} weeks`}</span>
                </div>
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Mode</span>
                  <span className="text-sm text-white font-sans">{project.mode === 'private' ? 'Private 1:1' : 'Cohort'}</span>
                </div>
                {project.mode !== 'private' && (
                  <div className="py-3.5 flex justify-between items-center">
                    <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Cohort Capacity</span>
                    <span className={`text-sm font-sans font-medium ${project.spots_remaining > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {project.spots_remaining > 0 ? `${project.spots_remaining} of ${project.capacity} spots remaining` : 'Full'}
                    </span>
                  </div>
                )}
                <div className="py-3.5 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Project Lab Fee</span>
                  <span className="text-sm font-mono font-medium text-cinema-gold">
                    {project.mentorship_fee ? `UGX ${Number(project.mentorship_fee).toLocaleString()}` : 'Included'}
                  </span>
                </div>
              </div>
            </Reveal>

            {acceptingApplications && (
              <Reveal delay={60}>
                <button
                  onClick={() => {
                    setShowForm(true)
                    setTimeout(() => document.getElementById('project-lab-apply')?.scrollIntoView({ behavior: 'smooth' }), 100)
                  }}
                  className="cinema-btn-primary rounded-none !w-full !py-4 !text-xs uppercase tracking-[0.18em] font-bold flex items-center justify-center gap-2 min-h-[48px]"
                >
                  <span>Apply for This Project</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Reveal>
            )}
          </div>
        </div>
      </div>

      {/* Application Form — Deliberate Wide Desktop Editorial Composition */}
      {acceptingApplications && (
        <section
          id="project-lab-apply"
          className="py-16 sm:py-24 border-t border-white/10 bg-cinema-black/40"
        >
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-20 items-start">

              {/* LEFT COLUMN: Project Context & Application Roadmap (Open Editorial, Cardless) */}
              <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-8">
                <Reveal>
                  <p className="text-xs uppercase tracking-[0.2em] text-cinema-gold font-sans font-semibold mb-2">
                    Project Lab &middot; Public Application
                  </p>
                  <h2 className="font-display text-xl sm:text-3xl text-white font-light tracking-tight">
                    Apply for {project.title}
                  </h2>
                  <p className="mt-3 text-sm text-cinema-soft/80 font-sans leading-relaxed">
                    You do not need an existing SMA student account to apply. Project Lab is open to any creative practitioner ready to produce portfolio-grade commercial work.
                  </p>
                </Reveal>

                {/* Structured Overview Metadata Strip — Open, Unboxed */}
                <div className="border-t border-b border-white/10 divide-y divide-white/5">
                  <div className="py-3 flex justify-between items-baseline">
                    <span className="text-xs font-mono uppercase tracking-wider text-cinema-muted">Discipline</span>
                    <span className="text-sm font-sans text-white font-medium">{project.discipline}</span>
                  </div>
                  <div className="py-3 flex justify-between items-baseline">
                    <span className="text-xs font-mono uppercase tracking-wider text-cinema-muted">Duration</span>
                    <span className="text-sm font-sans text-white">{project.duration || `${project.duration_weeks || 3} weeks`}</span>
                  </div>
                  <div className="py-3 flex justify-between items-baseline">
                    <span className="text-xs font-mono uppercase tracking-wider text-cinema-muted">Mode</span>
                    <span className="text-sm font-sans text-white capitalize">{project.mode} (Max {project.capacity || 6})</span>
                  </div>
                  <div className="py-3 flex justify-between items-baseline">
                    <span className="text-xs font-mono uppercase tracking-wider text-cinema-muted">Mentorship Fee</span>
                    <span className="text-sm font-mono font-medium text-cinema-gold">
                      {project.mentorship_fee ? `UGX ${Number(project.mentorship_fee).toLocaleString()}` : 'Included'}
                    </span>
                  </div>
                </div>

                {/* Application Roadmap — Open Editorial List */}
                <div className="pt-2 space-y-3">
                  <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-white">
                    Application Process
                  </h3>
                  <ol className="space-y-3 text-xs text-cinema-muted font-sans list-none">
                    <li className="flex items-start gap-3">
                      <span className="font-mono text-cinema-gold font-medium">01</span>
                      <span><strong className="text-white">Submit application:</strong> No login or upfront fee required to apply.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-mono text-cinema-gold font-medium">02</span>
                      <span><strong className="text-white">Portfolio review:</strong> SMA panel reviews your experience within 24–48 hours.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-mono text-cinema-gold font-medium">03</span>
                      <span><strong className="text-white">Acceptance & Invoicing:</strong> Official notification with invoice details dispatched.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-mono text-cinema-gold font-medium">04</span>
                      <span><strong className="text-white">Workspace Access:</strong> Participant enters live project journey with mentor.</span>
                    </li>
                  </ol>
                </div>
              </div>

              {/* RIGHT COLUMN: Application Form (Wide Editorial Canvas, Cardless) */}
              <div className="lg:col-span-7 lg:border-l lg:border-white/10 lg:pl-10 xl:pl-14">
                {submitted ? (
                  <Reveal delay={60}>
                    <div className="p-8 border border-green-500/30 bg-green-500/5 rounded-none space-y-4">
                      <div className="flex items-start gap-4">
                        <CheckCircle2 className="w-6 h-6 text-green-400 shrink-0 mt-0.5" />
                        <div className="space-y-2">
                          <h3 className="text-base text-white font-sans font-medium">Application Received</h3>
                          <p className="text-sm text-cinema-soft font-sans leading-relaxed">
                            Thank you for your application to the <strong>{project.title}</strong>. A confirmation email has been dispatched to <strong>{form.email}</strong>.
                          </p>
                          <p className="text-xs text-cinema-muted font-sans leading-relaxed pt-2 border-t border-green-500/20">
                            Our review panel will evaluate your details and notify you within 24 to 48 business hours. If accepted, you will receive onboarding instructions and invoice details to finalize your enrollment.
                          </p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ) : (
                  <div className="space-y-8">
                    <div className="pb-4 border-b border-white/10">
                      <h3 className="text-sm font-mono uppercase tracking-[0.2em] text-white">
                        Participant Application Form
                      </h3>
                      <p className="text-xs text-cinema-muted font-sans mt-1">
                        All fields marked with an asterisk (*) are required for panel evaluation.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">

                      {/* ── 01 / ABOUT YOU ── */}
                      <div className="space-y-4">
                        <div className="pb-2 border-b border-white/10">
                          <span className="text-[0.65rem] sm:text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold">
                            01 / About You
                          </span>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-xs text-cinema-muted font-sans uppercase tracking-wider mb-1.5" htmlFor="pl-first-name">
                              First Name *
                            </label>
                            <input
                              id="pl-first-name"
                              name="first_name"
                              type="text"
                              required
                              value={form.first_name}
                              onChange={handleInputChange}
                              className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-none text-white text-sm font-sans placeholder:text-cinema-muted/50 focus:border-cinema-gold/60 focus:outline-none transition-colors"
                              placeholder="e.g. Sarah"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-cinema-muted font-sans uppercase tracking-wider mb-1.5" htmlFor="pl-last-name">
                              Last Name *
                            </label>
                            <input
                              id="pl-last-name"
                              name="last_name"
                              type="text"
                              required
                              value={form.last_name}
                              onChange={handleInputChange}
                              className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-none text-white text-sm font-sans placeholder:text-cinema-muted/50 focus:border-cinema-gold/60 focus:outline-none transition-colors"
                              placeholder="e.g. Namubiru"
                            />
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-xs text-cinema-muted font-sans uppercase tracking-wider mb-1.5" htmlFor="pl-email">
                              Email Address *
                            </label>
                            <input
                              id="pl-email"
                              name="email"
                              type="email"
                              required
                              value={form.email}
                              onChange={handleInputChange}
                              className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-none text-white text-sm font-sans placeholder:text-cinema-muted/50 focus:border-cinema-gold/60 focus:outline-none transition-colors"
                              placeholder="sarah@example.com"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-cinema-muted font-sans uppercase tracking-wider mb-1.5" htmlFor="pl-phone">
                              Phone Number
                            </label>
                            <input
                              id="pl-phone"
                              name="phone"
                              type="tel"
                              value={form.phone}
                              onChange={handleInputChange}
                              className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-none text-white text-sm font-sans placeholder:text-cinema-muted/50 focus:border-cinema-gold/60 focus:outline-none transition-colors"
                              placeholder="+256 700 000 000"
                            />
                          </div>
                        </div>
                      </div>

                      {/* ── 02 / YOUR EXPERIENCE ── */}
                      <div className="space-y-4">
                        <div className="pb-2 border-b border-white/10">
                          <span className="text-[0.65rem] sm:text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold">
                            02 / Your Experience
                          </span>
                        </div>

                        <div>
                          <label className="block text-xs text-cinema-muted font-sans uppercase tracking-wider mb-1.5" htmlFor="pl-experience">
                            Relevant Experience & Current Equipment
                          </label>
                          <textarea
                            id="pl-experience"
                            name="experience"
                            rows="3"
                            value={form.experience}
                            onChange={handleInputChange}
                            className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-none text-white text-sm font-sans placeholder:text-cinema-muted/50 focus:border-cinema-gold/60 focus:outline-none transition-colors resize-none"
                            placeholder="Detail your photography background and equipment you currently shoot with..."
                          />
                        </div>
                      </div>

                      {/* ── 03 / YOUR WORK ── */}
                      <div className="space-y-4">
                        <div className="pb-2 border-b border-white/10">
                          <span className="text-[0.65rem] sm:text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold">
                            03 / Your Work
                          </span>
                        </div>

                        <div>
                          <label className="block text-xs text-cinema-muted font-sans uppercase tracking-wider mb-1.5" htmlFor="pl-portfolio">
                            Portfolio or Work URL
                          </label>
                          <input
                            id="pl-portfolio"
                            name="portfolio_url"
                            type="url"
                            value={form.portfolio_url}
                            onChange={handleInputChange}
                            className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-none text-white text-sm font-sans placeholder:text-cinema-muted/50 focus:border-cinema-gold/60 focus:outline-none transition-colors"
                            placeholder="https://instagram.com/... or https://behance.net/..."
                          />
                        </div>
                      </div>

                      {/* ── 04 / YOUR GOAL ── */}
                      <div className="space-y-4">
                        <div className="pb-2 border-b border-white/10">
                          <span className="text-[0.65rem] sm:text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold">
                            04 / Your Goal
                          </span>
                        </div>

                        <div>
                          <label className="block text-xs text-cinema-muted font-sans uppercase tracking-wider mb-1.5" htmlFor="pl-motivation">
                            What is your primary goal for this project?
                          </label>
                          <textarea
                            id="pl-motivation"
                            name="motivation"
                            rows="3"
                            value={form.motivation}
                            onChange={handleInputChange}
                            className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-none text-white text-sm font-sans placeholder:text-cinema-muted/50 focus:border-cinema-gold/60 focus:outline-none transition-colors resize-none"
                            placeholder="What specific outcome or commercial capability do you want to master?"
                          />
                        </div>
                      </div>

                      {submitError && (
                        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-none text-red-400 text-xs font-sans">
                          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                          <span>{submitError}</span>
                        </div>
                      )}

                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="cinema-btn-primary rounded-none !w-full !py-4 !text-xs uppercase tracking-[0.18em] font-bold flex items-center justify-center gap-2 min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submitting ? 'Submitting Application...' : 'Submit Application'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      <CinematicFooter />
    </div>
  )
}
